import { APIGetBillingPaymentSelection } from "../../service/api/APIBilling";
// React Query hooks for Billing Management
// Pattern: useGet for fetch data (useQuery), useCreate/useUpdate/useDelete for mutations (useMutation)
// Auto invalidate queries after mutation success
// Reference: BILLING_MANAGEMENT_API_GUIDE.md

import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import {
  APIEnsureBillingRounds,
  APIExportBillingReport,
  APIGetBillingPeriods,
  APIGetBillingRounds,
  APIGetPeriodRounds,
  APIGetBillingCustomers,
  APIGetBillingCustomerInvoices,
  APICreateBillingPayment,
  APIApproveBillingPayment,
  APIRejectBillingPayment,
  APIGetBillingPayments,
  APIGetBillingPaymentDetail,
  APIUpdateBillingPayment,
  APIUpdatePaymentEvidenceVerification,
  APIDeleteBillingPayment,
  APIUploadPaymentEvidence,
  APICreateBillingRound,
  APIUpdateBillingRound,
  APIDeleteBillingRound,
  APIClearUnpaidBillingRounds,
  APIRegenerateBillingRounds,
  APIGetCustomersBillingSummary,
} from "../../service/api/APIBilling";
import {
  IBillingReportParams,
  EBillingQueryKeys,
  IBillingPeriodsQueryParams,
  IBillingRoundsQueryParams,
  IBillingCustomersQueryParams,
  IBillingInvoicesQueryParams,
  ICreatePaymentDto,
  ICancelBillingPaymentDto,
  IUpdatePaymentDto,
  IUpdatePaymentEvidenceVerificationDto,
  IRejectBillingPaymentDto,
  IBillingPaymentsQueryParams,
  ICreateBillingRoundDto,
  IUpdateBillingRoundDto,
  IRegenerateBillingRoundsDto,
  IBillingCustomersSummaryQueryParams,
  IUploadPaymentEvidenceOptions,
} from "../../compiler/billing.interface";

// ============================================================
// QUERY HOOKS (Data Fetching)
// ============================================================

/**
 * ดึงรายการรอบบิลแบบ server-side group by ช่วงวันที่
 * Stale time: 2 minutes
 */
export const useGetBillingPeriods = (params?: IBillingPeriodsQueryParams) => {
  const { enqueueSnackbar } = useSnackbar();
  return useQuery(
    [EBillingQueryKeys.GET_BILLING_PERIODS, params],
    () => APIGetBillingPeriods(params),
    {
      staleTime: 2 * 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: any) => {
        enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถดึงรายการรอบบิลได้", {
          variant: "error",
        });
      },
    }
  );
};

/**
 * ดึงรายละเอียดรายลูกค้าในช่วงวันที่ (lazy — enabled เมื่อ enabled=true)
 */
export const useGetPeriodRounds = (startDate: string, endDate: string, enabled: boolean) => {
  const { enqueueSnackbar } = useSnackbar();
  return useQuery(
    [EBillingQueryKeys.GET_PERIOD_ROUNDS, startDate, endDate],
    () => APIGetPeriodRounds(startDate, endDate),
    {
      enabled: enabled && !!startDate && !!endDate,
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: any) => {
        enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถดึงรายละเอียดรอบบิลได้", {
          variant: "error",
        });
      },
    }
  );
};

/**
 * สรุปยอดค้างชำระรายลูกค้าข้ามรอบบิล (cross-period)
 */
export const useGetCustomersBillingSummary = (params?: IBillingCustomersSummaryQueryParams) => {
  const { enqueueSnackbar } = useSnackbar();
  return useQuery(
    [EBillingQueryKeys.GET_CUSTOMERS_BILLING_SUMMARY, params],
    () => APIGetCustomersBillingSummary(params),
    {
      staleTime: 2 * 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: any) => {
        enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถดึงข้อมูลลูกค้าได้", {
          variant: "error",
        });
      },
    }
  );
};

/**
 * ดึงรายการรอบวางบิล
 * Stale time: 2 minutes
 */
export const useGetBillingRounds = (params?: IBillingRoundsQueryParams) => {
  const { enqueueSnackbar } = useSnackbar();

  return useQuery(
    [EBillingQueryKeys.GET_BILLING_ROUNDS, params],
    () => APIGetBillingRounds(params),
    {
      staleTime: 2 * 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: any) => {
        enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถดึงรายการรอบวางบิลได้", {
          variant: "error",
        });
      },
    }
  );
};

/**
 * ดึงรายการลูกค้าในรอบวางบิลที่เลือก
 * Enabled เมื่อมี start_date + end_date
 * Stale time: 2 minutes
 */
export const useGetBillingCustomers = (params: IBillingCustomersQueryParams) => {
  const { enqueueSnackbar } = useSnackbar();

  return useQuery(
    [EBillingQueryKeys.GET_BILLING_CUSTOMERS, params],
    () => APIGetBillingCustomers(params),
    {
      enabled: !!params.start_date && !!params.end_date,
      staleTime: 2 * 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: any) => {
        enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถดึงรายการลูกค้าได้", {
          variant: "error",
        });
      },
    }
  );
};

/**
 * ดึงรายการ Invoice ของลูกค้ารายนั้นในรอบวางบิล
 * Enabled เมื่อมี customer_code + start_date + end_date
 * Stale time: 2 minutes
 */
export const useGetBillingCustomerInvoices = (params: IBillingInvoicesQueryParams) => {
  const { enqueueSnackbar } = useSnackbar();

  return useQuery(
    [EBillingQueryKeys.GET_BILLING_INVOICES, params],
    () => APIGetBillingCustomerInvoices(params),
    {
      enabled: !!params.customer_code && !!params.start_date && !!params.end_date,
      staleTime: 2 * 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: any) => {
        enqueueSnackbar(
          error?.response?.data?.message || "ไม่สามารถดึงรายการ Invoice ของลูกค้าได้",
          { variant: "error" }
        );
      },
    }
  );
};

/**
 * ดึงรายการชำระเงิน
 * Stale time: 1 minute
 */
export const useGetBillingPayments = (params?: IBillingPaymentsQueryParams, enabled = true) => {
  const { enqueueSnackbar } = useSnackbar();

  return useQuery(
    [EBillingQueryKeys.GET_BILLING_PAYMENTS, params],
    () => APIGetBillingPayments(params),
    {
      enabled,
      staleTime: 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: any) => {
        enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถดึงรายการชำระเงินได้", {
          variant: "error",
        });
      },
    }
  );
};

/**
 * ดึงรายละเอียดการชำระเงิน
 * Enabled เมื่อมี uuid
 * Stale time: 5 minutes
 */
export const useGetBillingPaymentDetail = (uuid: string, enabled = true) => {
  const { enqueueSnackbar } = useSnackbar();

  return useQuery(
    [EBillingQueryKeys.GET_BILLING_PAYMENT_DETAIL, uuid],
    () => APIGetBillingPaymentDetail(uuid),
    {
      enabled: !!uuid && enabled,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: any) => {
        enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถดึงรายละเอียดการชำระเงินได้", {
          variant: "error",
        });
      },
    }
  );
};

// ============================================================
// MUTATION HOOKS
// ============================================================

/**
 * สร้างรายการชำระเงินใหม่
 * Invalidates: rounds, customers, payments
 */
export const useCreateBillingPayment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation((dto: ICreatePaymentDto) => APICreateBillingPayment(dto), {
    onSuccess: (data) => {
      enqueueSnackbar(data.message || "บันทึกการชำระเงินสำเร็จ", { variant: "success" });
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_ROUNDS]);
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_CUSTOMERS]);
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_INVOICES]);
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PAYMENTS]);
      queryClient.invalidateQueries([EBillingQueryKeys.GET_CUSTOMERS_BILLING_SUMMARY]);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message;
      enqueueSnackbar(
        Array.isArray(message) ? message.join(", ") : message || "ไม่สามารถบันทึกการชำระเงินได้",
        { variant: "error" }
      );
    },
  });
};

/**
 * อนุมัติการชำระเงิน
 */
export const useApproveBillingPayment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation((uuid: string) => APIApproveBillingPayment(uuid), {
    onSuccess: (data) => {
      enqueueSnackbar(data.message || "อนุมัติการชำระเงินสำเร็จ", { variant: "success" });
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_ROUNDS]);
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PERIODS]);
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_CUSTOMERS]);
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_INVOICES]);
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PAYMENTS]);
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PAYMENT_DETAIL]);
      queryClient.invalidateQueries([EBillingQueryKeys.GET_CUSTOMERS_BILLING_SUMMARY]);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message;
      enqueueSnackbar(
        Array.isArray(message) ? message.join(", ") : message || "ไม่สามารถอนุมัติการชำระเงินได้",
        { variant: "error" }
      );
    },
  });
};

/**
 * ไม่อนุมัติการชำระเงิน
 */
export const useRejectBillingPayment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(
    ({ uuid, dto }: { uuid: string; dto: IRejectBillingPaymentDto }) =>
      APIRejectBillingPayment(uuid, dto),
    {
      onSuccess: (data) => {
        enqueueSnackbar(data.message || "ไม่อนุมัติการชำระเงินสำเร็จ", { variant: "success" });
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PAYMENTS]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PAYMENT_DETAIL]);
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message;
        enqueueSnackbar(
          Array.isArray(message)
            ? message.join(", ")
            : message || "ไม่สามารถไม่อนุมัติการชำระเงินได้",
          { variant: "error" }
        );
      },
    }
  );
};

/**
 * แก้ไขรายการชำระเงิน
 */
export const useUpdateBillingPayment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(
    ({ uuid, dto }: { uuid: string; dto: IUpdatePaymentDto }) => APIUpdateBillingPayment(uuid, dto),
    {
      onSuccess: (data) => {
        enqueueSnackbar(data.message || "แก้ไขรายการชำระเงินสำเร็จ", { variant: "success" });
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_ROUNDS]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_CUSTOMERS]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PAYMENTS]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PAYMENT_DETAIL]);
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message;
        enqueueSnackbar(
          Array.isArray(message)
            ? message.join(", ")
            : message || "ไม่สามารถแก้ไขรายการชำระเงินได้",
          { variant: "error" }
        );
      },
    }
  );
};

/**
 * บันทึกผล OCR/manual verification ของหลักฐาน
 */
export const useUpdatePaymentEvidenceVerification = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(
    ({ evidenceUuid, dto }: { evidenceUuid: string; dto: IUpdatePaymentEvidenceVerificationDto }) =>
      APIUpdatePaymentEvidenceVerification(evidenceUuid, dto),
    {
      onSuccess: (data) => {
        enqueueSnackbar(data.message || "บันทึกผลตรวจหลักฐานสำเร็จ", { variant: "success" });
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PAYMENTS]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PAYMENT_DETAIL]);
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message;
        enqueueSnackbar(
          Array.isArray(message)
            ? message.join(", ")
            : message || "ไม่สามารถบันทึกผลตรวจหลักฐานได้",
          { variant: "error" }
        );
      },
    }
  );
};

/**
 * ลบรายการชำระเงิน
 */
export const useDeleteBillingPayment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(
    ({ uuid, dto }: { uuid: string; dto?: ICancelBillingPaymentDto }) =>
      APIDeleteBillingPayment(uuid, dto),
    {
      onSuccess: (data) => {
        enqueueSnackbar(data.message || "ยกเลิกรายการรับชำระสำเร็จ", { variant: "success" });
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_ROUNDS]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PERIODS]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_CUSTOMERS]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_INVOICES]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PAYMENTS]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PAYMENT_DETAIL]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_CUSTOMERS_BILLING_SUMMARY]);
      },
      onError: (error: any) => {
        enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถยกเลิกรายการรับชำระได้", {
          variant: "error",
        });
      },
    }
  );
};

/**
 * อัปโหลดหลักฐานเพิ่มเติม
 */
export const useUploadPaymentEvidence = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(
    ({
      paymentUuid,
      files,
      evidenceSource,
      enforceCustomerScope,
    }: {
      paymentUuid: string;
      files: File[];
      evidenceSource?: IUploadPaymentEvidenceOptions["evidence_source"];
      enforceCustomerScope?: boolean;
    }) =>
      APIUploadPaymentEvidence(paymentUuid, files, {
        evidence_source: evidenceSource,
        enforce_customer_scope: enforceCustomerScope,
      }),
    {
      onSuccess: (data) => {
        enqueueSnackbar(data.message || "อัปโหลดหลักฐานสำเร็จ", { variant: "success" });
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PAYMENTS]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PAYMENT_DETAIL]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_INVOICES]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_CUSTOMERS_BILLING_SUMMARY]);
      },
      onError: (error: any) => {
        enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถอัปโหลดหลักฐานได้", {
          variant: "error",
        });
      },
    }
  );
};

// ============================================================
// ROUND MANAGEMENT HOOKS
// ============================================================

export const useCreateBillingRound = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation((dto: ICreateBillingRoundDto) => APICreateBillingRound(dto), {
    onSuccess: () => {
      enqueueSnackbar("สร้างรอบบิลสำเร็จ", { variant: "success" });
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_ROUNDS]);
    },
    onError: (error: any) => {
      enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถสร้างรอบบิลได้", {
        variant: "error",
      });
    },
  });
};

export const useUpdateBillingRound = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(
    ({ uuid, dto }: { uuid: string; dto: IUpdateBillingRoundDto }) =>
      APIUpdateBillingRound(uuid, dto),
    {
      onSuccess: () => {
        enqueueSnackbar("แก้ไขรอบบิลสำเร็จ", { variant: "success" });
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_ROUNDS]);
      },
      onError: (error: any) => {
        enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถแก้ไขรอบบิลได้", {
          variant: "error",
        });
      },
    }
  );
};

export const useDeleteBillingRound = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation((uuid: string) => APIDeleteBillingRound(uuid), {
    onSuccess: (data) => {
      enqueueSnackbar(data.message || "ลบรอบบิลสำเร็จ", { variant: "success" });
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_ROUNDS]);
    },
    onError: (error: any) => {
      enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถลบรอบบิลได้", {
        variant: "error",
      });
    },
  });
};

export const useClearUnpaidBillingRounds = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(() => APIClearUnpaidBillingRounds(), {
    onSuccess: (data) => {
      enqueueSnackbar(data.message, { variant: "success" });
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_ROUNDS]);
      queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PERIODS]);
    },
    onError: (error: any) => {
      enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถเคลียร์รอบบิลได้", {
        variant: "error",
      });
    },
  });
};

export const useRegenerateBillingRounds = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation((dto: IRegenerateBillingRoundsDto) => APIRegenerateBillingRounds(dto), {
    onSuccess: (data) => {
      if (!data.dry_run) {
        enqueueSnackbar(data.message, { variant: "success" });
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_ROUNDS]);
        queryClient.invalidateQueries([EBillingQueryKeys.GET_BILLING_PERIODS]);
      }
    },
    onError: (error: any) => {
      enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถ regenerate รอบบิลได้", {
        variant: "error",
      });
    },
  });
};

export const useEnsureBillingRounds = () => {
  const client = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(APIEnsureBillingRounds, {
    onSuccess: (result) => {
      enqueueSnackbar(`เพิ่มรอบที่ขาด ${result.data.created} รอบ`, { variant: "success" });
      [EBillingQueryKeys.GET_BILLING_PERIODS, EBillingQueryKeys.GET_BILLING_ROUNDS,
       EBillingQueryKeys.GET_PERIOD_ROUNDS, EBillingQueryKeys.GET_CUSTOMERS_BILLING_SUMMARY]
        .forEach(key => client.invalidateQueries([key]));
    },
    onError: (error: any) => enqueueSnackbar(error?.response?.data?.message || "ไม่สามารถเพิ่มรอบบิลได้", { variant: "error" }),
  });
};
export const useExportBillingReport = () => {
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(async (params: IBillingReportParams) => {
    const blob = await APIExportBillingReport(params);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `billing-${params.customer_code}-${params.start_date}-${params.end_date}.xlsx`;
    link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, { onError: () => enqueueSnackbar("ไม่สามารถส่งออกรายงานได้ กรุณาตรวจสิทธิ์และลองอีกครั้ง", { variant: "error" }) });
};

/** Explicit fresh read for selection/revalidation, never cached across franchise contexts. */
export const useReadBillingPaymentSelection = () => useMutation(APIGetBillingPaymentSelection);
